import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { FiClipboard, FiCheck, FiX } from 'react-icons/fi';
import styles from './Domains.module.css';
import Layout from '../../Layout/Layout';
import { URL as BASEURL } from '../../AxiosUrlGlobal';
import { toast } from 'react-toastify';

function Domains() {
    const [domains, setDomains] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [typeFilter, setTypeFilter] = useState('all');

    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalDomains, setTotalDomains] = useState(0);
    const [limit] = useState(20);

    const [stats, setStats] = useState({
        totalDomains: 0,
        customDomains: 0,
        subdomains: 0,
        sslEnabled: 0,
    });

    useEffect(() => {
        fetchDomains();
        // eslint-disable-next-line
    }, [currentPage, searchTerm, typeFilter]);

    const fetchDomains = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem('token');
            const userId = localStorage.getItem('id');

            const response = await axios.get(
                `${BASEURL}superadmin/domains/${userId}`,
                {
                    headers: { token },
                    params: {
                        page: currentPage,
                        limit: limit,
                        search: searchTerm,
                        typeFilter: typeFilter
                    }
                }
            );

            setDomains(response.data.domains);
            setTotalPages(response.data.pagination.totalPages);
            setTotalDomains(response.data.pagination.totalDomains);
            setStats(response.data.stats);
            setError('');
        } catch (err) {
            console.error('Error fetching domains:', err);
            setError('Failed to load domains');
            toast.error('Failed to load domains');
        } finally {
            setLoading(false);
        }
    };

    const copyToClipboard = (text) => {
        navigator.clipboard.writeText(text);
        toast.success('Copied!');
    };

    const formatDate = (date) => {
        if (!date) return 'N/A';
        return new Date(date).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };

    return (
        <Layout>
            <div className={styles.container}>
                <div className={styles.header}>
                    <h1>All Domains & Subdomains</h1>
                    <button onClick={fetchDomains} className={styles.refreshBtn} disabled={loading}>
                        {loading ? 'Loading...' : 'Refresh'}
                    </button>
                </div>

                <div className={styles.statsGrid}>
                    <div className={styles.statCard}>
                        <div className={styles.statValue}>{stats.totalDomains}</div>
                        <div className={styles.statLabel}>Total</div>
                    </div>
                    <div className={styles.statCard}>
                        <div className={styles.statValue}>{stats.customDomains}</div>
                        <div className={styles.statLabel}>Custom</div>
                    </div>
                    <div className={styles.statCard}>
                        <div className={styles.statValue}>{stats.subdomains}</div>
                        <div className={styles.statLabel}>Subdomains</div>
                    </div>
                    <div className={styles.statCard}>
                        <div className={styles.statValue}>{stats.sslEnabled}</div>
                        <div className={styles.statLabel}>SSL</div>
                    </div>
                </div>

                <div className={styles.controls}>
                    <input
                        type="text"
                        placeholder="Search by domain, subdomain, or store name..."
                        value={searchTerm}
                        onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
                        className={styles.searchInput}
                    />
                    <div className={styles.filters}>
                        <button
                            className={`${styles.filterBtn} ${typeFilter === 'all' ? styles.active : ''}`}
                            onClick={() => { setTypeFilter('all'); setCurrentPage(1); }}
                        >
                            All ({stats.totalDomains})
                        </button>
                        <button
                            className={`${styles.filterBtn} ${typeFilter === 'domain' ? styles.active : ''}`}
                            onClick={() => { setTypeFilter('domain'); setCurrentPage(1); }}
                        >
                            Custom ({stats.customDomains})
                        </button>
                        <button
                            className={`${styles.filterBtn} ${typeFilter === 'subdomain' ? styles.active : ''}`}
                            onClick={() => { setTypeFilter('subdomain'); setCurrentPage(1); }}
                        >
                            Subdomains ({stats.subdomains})
                        </button>
                    </div>
                </div>

                {loading ? (
                    <div className={styles.loading}>Loading domains...</div>
                ) : error ? (
                    <div className={styles.error}>{error}</div>
                ) : domains.length === 0 ? (
                    <div className={styles.noData}>No domains found</div>
                ) : (
                    <>
                        <div className={styles.tableContainer}>
                            <table className={styles.table}>
                                <thead>
                                    <tr>
                                        <th>Domain</th>
                                        <th>Store</th>
                                        <th>Owner</th>
                                        <th>Type</th>
                                        <th>SSL</th>
                                        <th>Status</th>
                                        <th>Created</th>
                                        <th>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {domains.map((domain) => (
                                        <tr key={domain._id}>
                                            <td>
                                                <div className={styles.domainCell}>
                                                    <span>{domain.SubDomain || domain.domain}</span>
                                                    <button
                                                        onClick={() => copyToClipboard(domain.domain)}
                                                        className={styles.copyBtn}
                                                    >
                                                        <FiClipboard />
                                                    </button>
                                                </div>
                                                {domain.SubDomain && (
                                                    <div className={styles.fullDomain}>{domain.domain}</div>
                                                )}
                                            </td>
                                            <td>{domain.store || 'N/A'}</td>
                                            <td>
                                                <div>
                                                    <div>{domain.owner?.username || 'Unknown'}</div>
                                                    <div className={styles.email}>{domain.owner?.email}</div>
                                                </div>
                                            </td>
                                            <td>
                                                <span className={domain.SubDomain ? styles.subdomain : styles.custom}>
                                                    {domain.SubDomain ? 'Subdomain' : 'Custom'}
                                                </span>
                                            </td>
                                            <td>
                                                <span className={domain.DomainSSL ? styles.ssl : styles.nossl}>
                                                    {domain.DomainSSL ? <><FiCheck style={{ verticalAlign: 'middle' }} /> SSL</> : <><FiX style={{ verticalAlign: 'middle' }} /> No SSL</>}
                                                </span>
                                            </td>
                                            <td>
                                                <span className={domain.status === 0 ? styles.active : styles.inactive}>
                                                    {domain.status === 0 ? 'Active' : 'Inactive'}
                                                </span>
                                            </td>
                                            <td>{formatDate(domain.date)}</td>
                                            <td>
                                                <a
                                                    href={`https://${domain.domain}`}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className={styles.visitBtn}
                                                >
                                                    Visit
                                                </a>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        {totalPages > 1 && (
                            <div className={styles.pagination}>
                                <button
                                    onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                                    disabled={currentPage === 1}
                                    className={styles.paginationBtn}
                                >
                                    Previous
                                </button>
                                <span className={styles.pageInfo}>
                                    Page {currentPage} of {totalPages} ({totalDomains} total)
                                </span>
                                <button
                                    onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                                    disabled={currentPage === totalPages}
                                    className={styles.paginationBtn}
                                >
                                    Next
                                </button>
                            </div>
                        )}
                    </>
                )}
            </div>
        </Layout>
    );
}

export default Domains;
